/**
 * The MUI component strings for the active UI locale.
 *
 * MUI renders its own default strings in English unless the theme carries a
 * locale: Autocomplete's "No options" and "Loading" text and its Clear, Open
 * and Close labels; TablePagination's "Rows per page:" and "1-10 of 100" (with
 * an en dash in MUI's own text); the Pagination and Breadcrumbs labels; Alert's
 * close label. No SPA passed one, so the Hungarian UI showed all of them in
 * English (reported 2026-09-29 on CreateWorkspaceDialog's empty Autocomplete;
 * Tibor's standing rule of that day: English UI text is fixed at once, or
 * hidden).
 *
 * `hu` takes MUI's own `huHU` with its two gaps closed:
 * - `labelDisplayedRows` is commented out in `huHU`, so the English
 *   "{from}-{to} of {count}" survived, with an en dash (U+2013). Here it is
 *   Hungarian, with the ASCII hyphen: display text never carries an en or em
 *   dash.
 * - Autocomplete `loadingText` is "Töltés" plus an ellipsis glyph in `huHU`.
 *   The UI's word is "Betöltés...", with three dots as in most of the catalog.
 * `en` keeps MUI's English defaults except `labelDisplayedRows`, whose MUI
 * default carries the same en dash; it gets the ASCII hyphen too.
 * These are literals of MUI's theme locale object, not Lingui messages. MUI
 * reads them from the theme, so they live beside `huHU` and need no catalog
 * entry.
 *
 * @module theme/muiLocale
 */
import { enUS, huHU, type Localization } from "@mui/material/locale";

/** MUI's `huHU`, with the untranslated and dash-bearing strings replaced. */
export const verisaHuHU: Localization = {
  components: {
    ...huHU.components,
    MuiTablePagination: {
      defaultProps: {
        ...huHU.components?.MuiTablePagination?.defaultProps,
        labelDisplayedRows: ({ from, to, count }) =>
          `${from}-${to} / ${count !== -1 ? count : `több mint ${to}`}`,
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        ...huHU.components?.MuiAutocomplete?.defaultProps,
        loadingText: "Betöltés...",
      },
    },
  },
};

/** MUI's `enUS`, with the en dash of the displayed-rows label replaced. */
export const verisaEnUS: Localization = {
  components: {
    ...enUS.components,
    MuiTablePagination: {
      defaultProps: {
        ...enUS.components?.MuiTablePagination?.defaultProps,
        labelDisplayedRows: ({ from, to, count }) =>
          `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`,
      },
    },
  },
};

/**
 * The MUI localization for a UI locale: Hungarian for `hu`, English for every
 * other value (the UI supports `hu` and `en`).
 */
export function muiLocalizationFor(locale: string | undefined): Localization {
  return locale === "hu" ? verisaHuHU : verisaEnUS;
}
